import type { ValueGetterParams, ValueSetterParams } from 'ag-grid-community'
import type { Product, Review } from '@skop-app/lib-domain/types'
import { Toast } from '@skop-app/lib-front'
import type { ProductStatus } from '@skop-app/lib-types'
import {
  applyDimensionInput,
  DIMENSION_INPUT_FALLBACK_MESSAGE,
  getDimensionAxisFromField,
  getDimensionInputValue,
  getDimensionSortTuple,
} from '@/domain/product/persistence/dimensionRules'
import {
  propagateGroupValue,
  updatePropertyInClone,
} from '@/components/Grid/utils/grid.utils'
import {
  getCellEditorProperty,
  isCustomField,
  isReferencesView,
} from '@/domain/product/persistence/propertyRules'
import {
  normalizeProductDateValue,
  serializeProductDateValue,
  validateProductDateRange,
} from '@/domain/product/persistence/dateRules'
import {
  normalizeProductLinkValue,
  validateProductLinkValue,
} from '@/domain/product/persistence/linkRules'
import {
  getAggregateReviewStatusForElement,
  getReviewGridProjectionFromContext,
} from '@/utils/reviews'

export const productReviewValueGetter = ({
  node,
  context,
}: ValueGetterParams<Product, unknown>): string | undefined => {
  const elementID = node?.data?.id
  if (!elementID) return undefined
  return getAggregateReviewStatusForElement(
    elementID,
    getReviewGridProjectionFromContext(context).reviewsByElementId as
      | Record<string, Review[]>
      | undefined,
  )
}

const translateFromContext = (
  context: unknown,
  key: string,
  fallback: string,
): string => {
  if (!context || typeof context !== 'object') return fallback
  const translator = (context as { t?: (translationKey: string) => string }).t
  if (typeof translator !== 'function') return fallback

  try {
    const translated = translator(key)
    return translated && translated !== key ? translated : fallback
  } catch {
    return fallback
  }
}

const getPropertyValues = (
  data: ProductStatus | undefined,
  key: string,
): string[] | undefined => {
  if (!data) return undefined

  const property = data.properties?.find((p) => p.key === key)
  if (property?.values) return property.values

  const direct = (data as Record<string, unknown>)[key]
  if (Array.isArray(direct) && direct.every((v) => typeof v === 'string')) {
    return direct as string[]
  }
  if (typeof direct === 'string') return [direct]
  return undefined
}

export const dateValueGetter = (
  params: ValueGetterParams<ProductStatus, unknown>,
): Date | null => {
  const key = params.colDef.field ? `${params.colDef.field}` : ''
  if (!key) return null

  const values = getPropertyValues(params.data, key)
  const raw = values?.[0]
  return normalizeProductDateValue(raw) ?? null
}

export const dimensionValueGetter = (
  params: ValueGetterParams<ProductStatus, unknown>,
): string | null => {
  const key = params.colDef.field ? `${params.colDef.field}` : ''
  const axis = getDimensionAxisFromField(key)
  if (!axis) return null

  const value = getDimensionInputValue(
    params.data as Record<string, unknown> | undefined,
    axis,
  )
  return value || null
}

export const dimensionValueComparator = (
  valueA: unknown,
  valueB: unknown,
): number => {
  const left = getDimensionSortTuple(valueA)
  const right = getDimensionSortTuple(valueB)

  if (left.empty && right.empty) return 0
  if (left.empty) return 1
  if (right.empty) return -1
  if (left.min !== right.min) return left.min - right.min
  return left.max - right.max
}

export const dimensionValueSetter = (
  params: ValueSetterParams<ProductStatus, unknown>,
) => {
  const { data, newValue, oldValue, colDef } = params
  const key = colDef.field ? `${colDef.field}` : ''
  const axis = getDimensionAxisFromField(key)
  if (!axis || !data) return false
  if (oldValue === newValue) return false

  if (propagateGroupValue(params, key)) return true

  // Leaf rows are updated in place here; AG Grid persists the mutation when
  // the setter returns true, while grouped rows delegate through setDataValue above.
  const parsed = applyDimensionInput(
    data as Record<string, unknown>,
    axis,
    newValue,
  )
  if (parsed.kind === 'invalid') {
    Toast.warning(DIMENSION_INPUT_FALLBACK_MESSAGE)
    return false
  }

  return true
}

export const dateValueSetter = (
  params: ValueSetterParams<ProductStatus, unknown>,
) => {
  const { data, newValue, oldValue, colDef, context } = params
  const key = colDef.field ? `${colDef.field}` : ''
  if (!key || oldValue === newValue) return false

  if (propagateGroupValue(params, key)) return true

  if (!data) return false

  if (key === 'startDate' || key === 'endDate') {
    const otherKey = key === 'startDate' ? 'endDate' : 'startDate'

    const otherValues = getPropertyValues(data, otherKey)
    const newDateInput =
      typeof newValue === 'string' || newValue instanceof Date ? newValue : null
    const validationError = validateProductDateRange({
      field: key,
      value: newDateInput,
      otherValue: otherValues?.[0],
    })

    if (validationError) {
      Toast.warning(
        translateFromContext(
          context,
          validationError.translationKey,
          validationError.fallbackMessage,
        ),
      )
      return false
    }
  }

  const nextValue = serializeProductDateValue(
    typeof newValue === 'string' || newValue instanceof Date ? newValue : null,
  )
  const meta = getCellEditorProperty(colDef)

  if (isReferencesView(meta.view) || isCustomField(key)) {
    updatePropertyInClone(data, key, nextValue ? [nextValue] : [], meta.type)
  } else {
    ;(data as Record<string, unknown>)[key] = nextValue || null
  }

  return true
}

export const linkValueSetter = (
  params: ValueSetterParams<ProductStatus, unknown>,
) => {
  const { node, newValue, oldValue, colDef } = params
  const key = colDef.field ? `${colDef.field}` : ''
  if (!key || oldValue === newValue) return false

  if (propagateGroupValue(params, key)) return true

  const data = node?.data
  if (!data) return false

  const next = normalizeProductLinkValue(newValue)

  const validationError = validateProductLinkValue(next)
  if (validationError) {
    Toast.warning(validationError.fallbackMessage)
    return false
  }

  const meta = getCellEditorProperty(colDef)

  if (isReferencesView(meta.view) || isCustomField(key)) {
    updatePropertyInClone(data, key, next ? [next] : [], meta.type)
  } else {
    ;(data as Record<string, unknown>)[key] = next || null
  }

  return true
}
